/**
 * Formulario de reserva: permite crear o editar una reserva con datos
 * de vehículos y pasajeros. Guarda con POST /forms.
 */
import { useState, useRef, useEffect } from 'react';
import { Save, X, ChevronDown, ChevronUp, Ship } from 'lucide-react';
import { apiRequest } from '@/services/apiClient';
import VehicleSelector, { type VehiculosCounts, type PasajerosCounts } from './VehicleSelector';
import ReservaFieldList from './ReservaFieldList';
import SaveButton from './SaveButton';
import ScannerModal from './ScannerModal';
import styles from './ReservationForm.module.css';

export interface EditableFormItem {
    id: number;
    datos: {
        vehiculos?: Partial<VehiculosCounts>;
        pasajeros?: Partial<PasajerosCounts>;
        numeroCoche?: string;
        tipoVehiculo?: string;
        cantidadVehiculos?: string;
        remolques?: string;
        codigoReservas: string[];
        anotaciones?: string;
        embarqueId: string;
    };
    completado: boolean;
    completado_secundario?: boolean;
}

interface ReservationFormProps {
    onSaveSuccess?: (wasUpdate: boolean) => void;
    editData?: EditableFormItem | null;
    onCancelEdit?: () => void;
    selectedEmbarkation: { id: string; nombre: string };
    onEmbarkationChange: (row: { id: string; nombre: string }) => void;
    embarkations: { id: string | number; nombre: string }[];
    onHeaderContent?: (content: React.ReactNode) => void;
}

export default function ReservationForm({
    onSaveSuccess,
    editData,
    onCancelEdit,
    selectedEmbarkation,
    onEmbarkationChange,
    embarkations,
    onHeaderContent,
}: ReservationFormProps) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);

    const defaultVehiculos: VehiculosCounts = { coches: '0', motos: '0', camiones: '0', autobuses: '0', remolques: '0' };
    const defaultPasajeros: PasajerosCounts = { adultos: '0', niños: '0', bebes: '0' };

    const [vehiculos, setVehiculos] = useState<VehiculosCounts>(defaultVehiculos);
    const [pasajeros, setPasajeros] = useState<PasajerosCounts>(defaultPasajeros);
    const [numeroCoche, setNumeroCoche] = useState("");
    const [codigoReservas, setCodigoReservas] = useState<string[]>([""]);
    const [anotaciones, setAnotaciones] = useState("");

    const onEmbarkationChangeRef = useRef(onEmbarkationChange);
    onEmbarkationChangeRef.current = onEmbarkationChange;

    useEffect(() => {
        if (editData && editData.datos) {
            const { datos } = editData;
            const row = embarkations.find(e => e.id.toString() === datos.embarqueId.toString()) || embarkations[0];
            if (row) onEmbarkationChangeRef.current(row as { id: string; nombre: string });
            if (datos.vehiculos) {
                setVehiculos({ ...defaultVehiculos, ...datos.vehiculos });
            } else {
                const legacy = { ...defaultVehiculos };
                const tipo = datos.tipoVehiculo || 'coche';
                const cant = datos.cantidadVehiculos || '1';
                if (tipo === 'moto') legacy.motos = cant;
                else if (tipo === 'camion') legacy.camiones = cant;
                else if (tipo === 'autobus') legacy.autobuses = cant;
                else if (tipo === 'remolque') legacy.remolques = cant;
                else legacy.coches = cant;
                if (datos.remolques && datos.remolques !== '0') legacy.remolques = datos.remolques;
                setVehiculos(legacy);
            }
            setPasajeros({
                adultos: datos.pasajeros?.adultos ?? '0',
                niños: datos.pasajeros?.niños ?? '0',
                bebes: datos.pasajeros?.bebes ?? '0',
            });
            setNumeroCoche(datos.numeroCoche || "");
            setCodigoReservas(datos.codigoReservas || [""]);
            setAnotaciones(datos.anotaciones || "");
            if (isCollapsed) setIsCollapsed(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editData, embarkations]);

    const [formFeedback, setFormFeedback] = useState<string | null>(null);
    const [isScannerVisible, setIsScannerVisible] = useState(false);
    const [activeScannerIndex, setActiveScannerIndex] = useState<number | null>(null);

    const MAX_ANOTACIONES = 2000;
    const sidePad = typeof window !== 'undefined' && window.innerWidth < 400 ? 10 : 14;

    useEffect(() => {
        onHeaderContent?.(
            <button className={styles.pageHeaderDropdown} onClick={() => setIsDropdownVisible(true)}>
                <span className={styles.pageHeaderTitle}>{selectedEmbarkation.nombre}</span>
            </button>
        );
    }, [selectedEmbarkation.nombre, onHeaderContent]);

    const toggleCollapse = () => setIsCollapsed(prev => !prev);

    const handleReservaChange = (index: number, value: string) => {
        setCodigoReservas(prev => { const copy = [...prev]; copy[index] = value; return copy; });
    };
    const handleCameraPress = (index: number) => {
        setActiveScannerIndex(index);
        setIsScannerVisible(true);
    };
    const handleScanResult = (data: string) => {
        if (activeScannerIndex !== null) handleReservaChange(activeScannerIndex, data.trim());
    };
    const addReservaField = () => setCodigoReservas(prev => [...prev, ""]);
    const removeReservaField = (index: number) => {
        setCodigoReservas(prev => {
            if (prev.length === 1) {
                return [''];
            }
            return prev.filter((_, idx) => idx !== index);
        });
    };

    const handleSave = async () => {
        const tieneReserva = codigoReservas.some(c => c.trim() !== '');

        if (!tieneReserva) {
            setFormFeedback('Añade al menos un código de reserva.');
            return;
        }

        try {
            const formData = {
                embarqueId: String(selectedEmbarkation.id),
                vehiculos,
                pasajeros,
                numeroCoche,
                codigoReservas,
                anotaciones: anotaciones.slice(0, MAX_ANOTACIONES),
                comentarios: '',
            };
            const isUpdate = !!editData;
            await apiRequest('/forms', {
                method: 'POST',
                body: {
                    datos: formData,
                    completado: editData ? editData.completado : false,
                    completado_secundario: editData ? editData.completado_secundario === true : false,
                    id: editData ? editData.id : undefined,
                },
            });
            setVehiculos(defaultVehiculos);
            setPasajeros(defaultPasajeros);
            setNumeroCoche("");
            setCodigoReservas([""]);
            setAnotaciones("");
            onSaveSuccess?.(isUpdate);
        } catch (error) {
            console.error('[ReservationForm] Error guardando:', error);
        }
    };

    return (
        <div className={styles.card}>
                <div className={styles.header}>
                    <div className={styles.headerEdge} aria-hidden />
                    <button
                        type="button"
                        className={styles.headerPressable}
                        onClick={toggleCollapse}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expandir formulario' : 'Contraer formulario'}
                    >
                        <span className={styles.headerTitle}>{editData ? 'Editar formulario' : 'Crear formulario'}</span>
                    </button>
                    <button
                        type="button"
                        className={styles.headerChevronBtn}
                        onClick={e => {
                            e.stopPropagation();
                            toggleCollapse();
                        }}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expandir formulario' : 'Contraer formulario'}
                    >
                        {isCollapsed ? (
                            <ChevronDown size={20} color="white" strokeWidth={2.25} aria-hidden />
                        ) : (
                            <ChevronUp size={20} color="white" strokeWidth={2.25} aria-hidden />
                        )}
                    </button>
                </div>

                {formFeedback && (
                    <div
                        className={styles.messageModalLayer}
                        onClick={() => setFormFeedback(null)}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Aviso"
                    >
                        <div className={styles.messageModalBox} onClick={e => e.stopPropagation()}>
                            <p className={styles.messageModalText}>{formFeedback}</p>
                            <div className={styles.messageModalActions}>
                                <button type="button" className={styles.messageModalOk} onClick={() => setFormFeedback(null)}>
                                    Aceptar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isDropdownVisible && (
                    <div className={styles.modalOverlay} onClick={() => setIsDropdownVisible(false)}>
                        <div className={styles.embarkPanel} onClick={e => e.stopPropagation()}>
                            <div className={styles.embarkPanelHeader}>
                                <span className={styles.embarkPanelTitle}>Selecciona un embarque</span>
                                <button
                                    type="button"
                                    className={styles.embarkPanelCloseBtn}
                                    onClick={() => setIsDropdownVisible(false)}
                                    aria-label="Cerrar"
                                >
                                    <X size={18} color="currentColor" strokeWidth={2.25} />
                                </button>
                            </div>
                            <div className={styles.embarkPanelSections}>
                                <div className={styles.embarkOptionStack}>
                                        {embarkations.map(item => {
                                            const active = selectedEmbarkation.id === item.id;
                                            return (
                                                <button
                                                    key={item.id.toString()}
                                                    type="button"
                                                    className={`${styles.embarkOptionBtn} ${active ? styles.embarkOptionBtnActive : ''}`}
                                                    onClick={() => {
                                                        onEmbarkationChange(item as { id: string; nombre: string });
                                                        setIsDropdownVisible(false);
                                                    }}
                                                >
                                                    <Ship size={16} color="currentColor" strokeWidth={2} />
                                                    <span className={styles.embarkOptionBtnLabel}>{item.nombre}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                            </div>
                        </div>
                    </div>
                )}

                {!isCollapsed && (
                    <div className={styles.body} style={{ padding: `12px ${sidePad}px ${sidePad}px` }}>
                        <VehicleSelector
                            vehiculos={vehiculos}
                            onVehiculosChange={setVehiculos}
                            pasajeros={pasajeros}
                            onPasajerosChange={setPasajeros}
                            numeroCoche={numeroCoche}
                            onNumeroCocheChange={setNumeroCoche}
                        />

                        <ReservaFieldList
                            codigoReservas={codigoReservas}
                            onReservaChange={handleReservaChange}
                            onAddReservaField={addReservaField}
                            onRemoveReservaField={removeReservaField}
                            onCameraPress={handleCameraPress}
                            anotaciones={anotaciones}
                            onAnotacionesChange={t => setAnotaciones(t.slice(0, MAX_ANOTACIONES))}
                        />

                        {editData ? (
                            <div className={styles.actionsRow}>
                                <button type="button" className={styles.primaryButton} onClick={handleSave}>
                                    <Save size={18} color="white" className={styles.primaryButtonIcon} />
                                    <span className={styles.primaryButtonText}>Actualizar</span>
                                </button>
                                <button
                                    type="button"
                                    className={styles.cancelIconButton}
                                    onClick={() => { setVehiculos(defaultVehiculos); setPasajeros(defaultPasajeros); setNumeroCoche(""); setCodigoReservas([""]); setAnotaciones(""); onCancelEdit?.(); }}
                                    aria-label="Cancelar edición"
                                >
                                    <X size={20} color="currentColor" strokeWidth={2.25} />
                                </button>
                            </div>
                        ) : (
                            <SaveButton onPress={handleSave} />
                        )}
                    </div>
                )}

                {isScannerVisible && (
                    <ScannerModal
                        onClose={() => setIsScannerVisible(false)}
                        onScanned={handleScanResult}
                    />
                )}
        </div>
    );
}
